import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import {
  ORG_ROLES,
  isProductKey,
  type Member,
  type OrgRole,
  type ProductKey,
} from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { MAX_MEMBER_SEARCH_LENGTH, normalizeSearch } from '../../api';
import { ActionLink, AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, PagerButton, SavedNotice } from '../../components/feedback';
import { CheckboxGroup, ReasonField, SelectField, reasonError } from '../../components/fields';
import { EMPLOYEE_PRODUCTS, ProductGrantsField, ROLE_DESCRIPTIONS } from '../../components/grants';
import { DateText, ScrollTable, StatusBadge } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { ORG_ROLE_LABELS, formatCount, plural, productName, productShortName } from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { SEARCH_DEBOUNCE_MS, previousCursors } from '../OrganizationsPage';
import { carriedState } from './OrganizationLayout';
import { useOrganizationScope, useSupportAccessHint } from './scope';

const MEMBER_STATUSES = ['active', 'invited', 'suspended', 'removed'] as const;
type MemberStatus = (typeof MEMBER_STATUSES)[number];

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'removed', label: 'Removed' },
];

function isMemberStatus(value: string | null): value is MemberStatus {
  return value !== null && (MEMBER_STATUSES as readonly string[]).includes(value);
}

interface MembersView {
  query: string;
  status?: MemberStatus | undefined;
  product?: ProductKey | undefined;
  cursor?: string | undefined;
}

/** The query string of a members view: search, filters and the cursor of the page shown. */
export function membersSearch({ query, status, product, cursor }: MembersView): string {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (status) params.set('status', status);
  if (product) params.set('product', product);
  if (cursor) params.set('cursor', cursor);
  const search = params.toString();
  return search ? `?${search}` : '';
}

function rolesText(roles: readonly OrgRole[]): string {
  return roles.map((role) => ORG_ROLE_LABELS[role] ?? role).join(', ');
}

function ProductsCell({ products }: { products: Member['products'] }) {
  if (products.length === 0) return <span className="admin-muted">None</span>;
  return (
    <ul className="admin-inline-list">
      {products.map((product) => (
        <li key={product.productKey}>
          {productShortName(product.productKey)}
          {product.productRole ? <span className="admin-muted"> ({product.productRole})</span> : null}
        </li>
      ))}
    </ul>
  );
}

type MemberDialog =
  | { kind: 'edit'; member: Member }
  | { kind: 'suspend'; member: Member }
  | { kind: 'reactivate'; member: Member }
  | { kind: 'remove'; member: Member };

function MembersTable({
  members,
  busy,
  caption,
  canManage,
  onAction,
}: {
  members: readonly Member[];
  busy: boolean;
  caption: string;
  canManage: boolean;
  onAction: (dialog: MemberDialog) => void;
}) {
  return (
    <ScrollTable caption={caption} busy={busy} className="admin-table admin-table--members">
      <thead>
        <tr>
          <th scope="col">Member</th>
          <th scope="col">Roles</th>
          <th scope="col">Products</th>
          <th scope="col">Status</th>
          <th scope="col">Last seen</th>
          <th scope="col">Added</th>
          {canManage ? <th scope="col">Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {members.map((member) => (
          <tr key={member.membershipId}>
            <th scope="row">
              {member.displayName}
              <span className="admin-cell-secondary">{member.email}</span>
            </th>
            <td>{rolesText(member.roles)}</td>
            <td>
              <ProductsCell products={member.products} />
            </td>
            <td>
              <StatusBadge status={member.status} />
            </td>
            <td>
              {member.lastSeenAt ? (
                <DateText iso={member.lastSeenAt} />
              ) : (
                <span className="admin-muted">Never</span>
              )}
            </td>
            <td>
              <DateText iso={member.createdAt} />
            </td>
            {canManage ? (
              <td>
                {member.status === 'removed' ? (
                  <span className="admin-muted">None</span>
                ) : (
                  <div className="admin-row-actions">
                    <button
                      type="button"
                      className="admin-action"
                      aria-label={`Edit ${member.displayName}`}
                      onClick={() => onAction({ kind: 'edit', member })}
                    >
                      Edit
                    </button>
                    {member.status === 'suspended' ? (
                      <button
                        type="button"
                        className="admin-action"
                        aria-label={`Reactivate ${member.displayName}`}
                        onClick={() => onAction({ kind: 'reactivate', member })}
                      >
                        Reactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="admin-action"
                        aria-label={`Suspend ${member.displayName}`}
                        onClick={() => onAction({ kind: 'suspend', member })}
                      >
                        Suspend
                      </button>
                    )}
                    <button
                      type="button"
                      className="admin-action admin-action--danger"
                      aria-label={`Remove ${member.displayName}`}
                      onClick={() => onAction({ kind: 'remove', member })}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

/**
 * Members (/organizations/:id/members): one server page of 25 at a time (keyset cursor, the total from
 * platform-api), searched by name or email and filtered by status and product, all kept in the address.
 * Staff who may manage members edit roles and products, and suspend, reactivate or remove a membership with
 * a reason; suspending and removing end the person's sessions, and the confirmation says so.
 */
export function MembersTab() {
  const { organization, abilities } = useOrganizationScope();
  const hint = useSupportAccessHint();
  const { api } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const organizationId = organization.id;

  const query = normalizeSearch(params.get('q') ?? '', MAX_MEMBER_SEARCH_LENGTH);
  const rawStatus = params.get('status');
  const status = isMemberStatus(rawStatus) ? rawStatus : undefined;
  const rawProduct = params.get('product');
  const product = rawProduct && isProductKey(rawProduct) ? rawProduct : undefined;
  const cursor = params.get('cursor') || undefined;
  const previous = cursor ? previousCursors(location.state) : [];
  const pageNumber = previous ? previous.length + 1 : null;
  // The way back to the organization list travels in the history state; kept as a string so effects do not
  // rerun for a new object on every render.
  const backSearch = carriedState(location.state)?.listSearch;
  const searchId = useId();
  const [text, setText] = useState(query);

  // Follow the address when it changes from elsewhere (back and forward), keeping what was typed when it
  // already means the same search.
  useEffect(() => {
    setText((current) => (normalizeSearch(current, MAX_MEMBER_SEARCH_LENGTH) === query ? current : query));
  }, [query]);

  const go = (view: MembersView, previousCursorsOfView: string[] | null, replace: boolean) =>
    void navigate(
      { search: membersSearch(view) },
      {
        replace,
        state: {
          ...(backSearch !== undefined ? { listSearch: backSearch } : {}),
          ...(previousCursorsOfView ? { previous: previousCursorsOfView } : {}),
        },
      },
    );

  // A search is a refinement, not a page to come back to: it replaces the history entry once typing pauses.
  useEffect(() => {
    const next = normalizeSearch(text, MAX_MEMBER_SEARCH_LENGTH);
    if (next === query) return;
    const timer = window.setTimeout(
      () =>
        void navigate(
          { search: membersSearch({ query: next, status, product }) },
          { replace: true, state: backSearch !== undefined ? { listSearch: backSearch } : null },
        ),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [text, query, status, product, navigate, backSearch]);

  const key = `members\u0000${organizationId}\u0000${query}\u0000${status ?? ''}\u0000${product ?? ''}\u0000${cursor ?? ''}`;
  const read = useAdminRead(key, (signal) =>
    api.listMembers(
      organizationId,
      {
        ...(query ? { query } : {}),
        ...(status ? { status } : {}),
        ...(product ? { productKey: product } : {}),
        ...(cursor ? { cursor } : {}),
      },
      signal,
    ),
  );
  const current = read.dataKey === key ? read.data : null;
  const shown = read.failure ? null : read.data;
  const filtered = Boolean(query || status || product);

  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<MemberDialog>();
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);
  const [roles, setRoles] = useState<OrgRole[]>([]);
  const [rolesProblem, setRolesProblem] = useState<string | null>(null);
  const [grants, setGrants] = useState<Partial<Record<ProductKey, string>>>({});

  const open = (next: MemberDialog) => {
    setReason('');
    setReasonProblem(null);
    setRolesProblem(null);
    if (next.kind === 'edit') {
      setRoles([...next.member.roles]);
      setGrants(
        Object.fromEntries(next.member.products.map((p) => [p.productKey, p.productRole ?? ''])) as Partial<
          Record<ProductKey, string>
        >,
      );
    }
    dialog.show(next);
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = normalizeSearch(text, MAX_MEMBER_SEARCH_LENGTH);
    if (next !== query) go({ query: next, status, product }, null, true);
  };

  let statusText = '';
  if (read.loading) statusText = 'Loading members';
  else if (current) {
    const total = plural(current.total, 'member', 'members');
    statusText = filtered ? `${total} match` : total;
    if (pageNumber && (pageNumber > 1 || current.nextCursor))
      statusText += `, page ${formatCount(pageNumber)}`;
  }

  let results: ReactNode;
  if (read.failure) {
    results = (
      <AdminProblem
        failure={read.failure}
        subject="the members"
        onRetry={read.retry}
        supportAccess={hint}
        action={
          read.failure.kind === 'invalid-request' ? (
            <ActionLink primary to={{ search: membersSearch({ query, status, product }) }}>
              Go to the first page
            </ActionLink>
          ) : undefined
        }
      />
    );
  } else if (shown && shown.items.length > 0) {
    results = (
      <MembersTable
        members={shown.items}
        busy={read.loading}
        caption={pageNumber ? `Members, page ${formatCount(pageNumber)}` : 'Members'}
        canManage={abilities.manageMembers}
        onAction={open}
      />
    );
  } else if (current) {
    results = filtered ? (
      <EmptyPageState
        icon={<NavigationGlyph name="Search" />}
        title="No members match"
        description="Search looks at names and email addresses; the filters narrow by status and product."
        action={<ActionLink to={{ search: '' }}>Clear the search and filters</ActionLink>}
      />
    ) : cursor ? (
      <EmptyPageState
        icon={<NavigationGlyph name="Users" />}
        title="No more members"
        description="This page is past the end of the list."
        action={<ActionLink to={{ search: '' }}>Go to the first page</ActionLink>}
      />
    ) : (
      <EmptyPageState
        icon={<NavigationGlyph name="Users" />}
        title="No members yet"
        description="People join an organization by accepting an invitation."
      />
    );
  } else {
    results = <LoadingBlock label="Loading the members" />;
  }

  const nextCursor = current?.nextCursor ?? null;
  const showPager = !read.failure && shown !== null && (cursor !== undefined || shown.nextCursor !== null);
  const productOptions = [
    { value: '', label: 'Any product' },
    ...EMPLOYEE_PRODUCTS.map((key) => ({ value: key, label: productName(key) })),
  ];

  const target = dialog.target;
  const name = organization.displayName;
  let dialogView: ReactNode = null;
  if (target) {
    const person = target.member.displayName;
    const reasonField = <ReasonField value={reason} onChange={setReason} error={reasonProblem} />;
    const checkReason = () => {
      const problem = reasonError(reason);
      setReasonProblem(problem);
      return problem === null;
    };
    const common = { open: dialog.open, onClose: dialog.close };
    if (target.kind === 'edit') {
      const candidates = EMPLOYEE_PRODUCTS.filter(
        (key) =>
          organization.products.some((p) => p.productKey === key) ||
          target.member.products.some((p) => p.productKey === key),
      );
      dialogView = (
        <WriteDialog
          key={dialog.key}
          {...common}
          title={`Edit ${person}`}
          description={`Roles and products of ${person} in ${name}.`}
          submitLabel="Save roles and products"
          onSubmit={async () => {
            const rolesError = roles.length === 0 ? 'Choose at least one role.' : null;
            setRolesProblem(rolesError);
            const reasonOk = checkReason();
            if (rolesError || !reasonOk) return 'invalid';
            await api.updateMember(organizationId, target.member.membershipId, {
              roles,
              products: candidates
                .filter((key) => grants[key] !== undefined)
                .map((key) => ({ productKey: key, productRole: grants[key]?.trim() || null })),
              reason,
            });
            return undefined;
          }}
          onSaved={() => {
            setNotice(`The roles and products of ${person} are saved.`);
            read.retry();
          }}
        >
          <CheckboxGroup
            legend="Organization roles"
            choices={ORG_ROLES.map((role) => ({
              value: role,
              label: ORG_ROLE_LABELS[role],
              description: ROLE_DESCRIPTIONS[role],
            }))}
            values={roles}
            onChange={(values) => setRoles(values as OrgRole[])}
            error={rolesProblem}
          />
          {candidates.length > 0 ? (
            <ProductGrantsField candidates={candidates} grants={grants} onChange={setGrants} />
          ) : (
            <p className="admin-muted">The organization has no product to grant.</p>
          )}
          {reasonField}
        </WriteDialog>
      );
    } else if (target.kind === 'suspend') {
      dialogView = (
        <WriteDialog
          key={dialog.key}
          {...common}
          title={`Suspend ${person}?`}
          submitLabel="Suspend and end sessions"
          tone="danger"
          onSubmit={async () => {
            if (!checkReason()) return 'invalid';
            await api.updateMember(organizationId, target.member.membershipId, {
              status: 'suspended',
              reason,
            });
            return undefined;
          }}
          onSaved={() => {
            setNotice(`${person} is suspended and their sessions have ended.`);
            read.retry();
          }}
        >
          <p className="admin-dialog-warning">
            Suspending ends {person}&apos;s sessions now: they are signed out of Trace and cannot use {name}{' '}
            until someone reactivates the membership.
          </p>
          <p>Their roles and products stay as they are.</p>
          {reasonField}
        </WriteDialog>
      );
    } else if (target.kind === 'remove') {
      dialogView = (
        <WriteDialog
          key={dialog.key}
          {...common}
          title={`Remove ${person} from ${name}?`}
          submitLabel="Remove and end sessions"
          tone="danger"
          onSubmit={async () => {
            if (!checkReason()) return 'invalid';
            await api.updateMember(organizationId, target.member.membershipId, { status: 'removed', reason });
            return undefined;
          }}
          onSaved={() => {
            setNotice(`${person} is removed from ${name} and their sessions have ended.`);
            read.retry();
          }}
        >
          <p className="admin-dialog-warning">
            Removing ends {person}&apos;s sessions now and takes away their access to {name}.
          </p>
          <p>The audit log keeps their history.</p>
          {reasonField}
        </WriteDialog>
      );
    } else {
      dialogView = (
        <WriteDialog
          key={dialog.key}
          {...common}
          title={`Reactivate ${person}?`}
          submitLabel="Reactivate"
          onSubmit={async () => {
            if (!checkReason()) return 'invalid';
            await api.updateMember(organizationId, target.member.membershipId, { status: 'active', reason });
            return undefined;
          }}
          onSaved={() => {
            setNotice(`${person} is active again.`);
            read.retry();
          }}
        >
          <p>
            {person} can sign in to {name} again, with the roles and products the membership has.
          </p>
          {reasonField}
        </WriteDialog>
      );
    }
  }

  return (
    <>
      <SavedNotice message={notice} />
      <PageSection
        title="Members"
        description="People of this organization, with their roles, products and status."
      >
        <div className="admin-toolbar">
          <form role="search" className="admin-search" onSubmit={submitSearch}>
            <label htmlFor={searchId} className="admin-sr-only">
              Search members
            </label>
            <span className="admin-search__icon" aria-hidden="true">
              <NavigationGlyph name="Search" />
            </span>
            <input
              id={searchId}
              type="search"
              value={text}
              onChange={(event) => setText(event.currentTarget.value)}
              maxLength={MAX_MEMBER_SEARCH_LENGTH}
              placeholder="Search by name or email"
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="search"
            />
          </form>
          <div className="admin-filters">
            <SelectField
              label="Status"
              value={status ?? ''}
              options={STATUS_OPTIONS}
              onChange={(value) =>
                go({ query, status: isMemberStatus(value) ? value : undefined, product }, null, true)
              }
            />
            <SelectField
              label="Product"
              value={product ?? ''}
              options={productOptions}
              onChange={(value) =>
                go({ query, status, product: isProductKey(value) ? value : undefined }, null, true)
              }
            />
          </div>
          <p className="admin-results-status" role="status" aria-live="polite">
            {statusText}
          </p>
        </div>
        {results}
        {showPager ? (
          <div className="admin-pager">
            {previous ? (
              <PagerButton
                onClick={() => {
                  if (!previous || previous.length === 0) return;
                  const before = previous[previous.length - 1];
                  go({ query, status, product, cursor: before || undefined }, previous.slice(0, -1), false);
                }}
                disabled={previous.length === 0 || read.loading}
              >
                Previous page
              </PagerButton>
            ) : (
              <PagerButton
                onClick={() => go({ query, status, product }, null, false)}
                disabled={read.loading}
              >
                First page
              </PagerButton>
            )}
            <PagerButton
              onClick={() => {
                if (nextCursor)
                  go(
                    { query, status, product, cursor: nextCursor },
                    previous ? [...previous, cursor ?? ''] : null,
                    false,
                  );
              }}
              disabled={nextCursor === null || read.loading}
            >
              Next page
            </PagerButton>
          </div>
        ) : null}
      </PageSection>
      {dialogView}
    </>
  );
}
